/**
 * QA API Client
 * 
 * Provides functions for QA checklist management, formatting verification,
 * grammar checking, and pre-submission validation.
 */

import { getAPIClient } from './client';

export interface QAChecklistItem {
  id: string;
  text: string;
  mandatory: boolean;
  description?: string;
  rubric_score?: boolean; // If true, requires 1-5 score from reviewer
}

export interface QAChecklistCategory {
  id: string;
  name: string;
  items: QAChecklistItem[];
}

export interface QAChecklistConfig {
  categories: QAChecklistCategory[];
}

export interface QAChecklistItemStatus {
  completed: boolean;
  completed_at?: string;
  notes?: string;
  reviewer_notes?: string;
  approval_score?: number; // 1-5 for reviewer items
}

export interface QAChecklistStatus {
  total_items: number;
  completed_items: number;
  completion_percentage: number;
  all_completed: boolean;
  checklist_data: {
    items?: {
      [categoryId: string]: {
        [itemId: string]: QAChecklistItemStatus;
      };
    };
    completion_percentage?: number;
    total_items?: number;
    completed_items?: number;
    completed_at?: string;
    completed_by?: string;
    overall_score?: number; // For reviewer checklist
  };
}

export interface QAChecklistResponse {
  config: QAChecklistConfig;
  status: QAChecklistStatus;
}

export interface ChecklistItemUpdate {
  category_id: string;
  item_id: string;
  completed: boolean;
  notes?: string;
  rubric_score?: number; // 1-5 for reviewer items
}

export interface GrammarError {
  type: 'grammar' | 'spelling' | 'tone' | 'style';
  severity: 'error' | 'warning' | 'suggestion';
  line_number?: number;
  position?: { start: number; end: number };
  original_text: string;
  suggested_text: string;
  explanation: string;
}

export interface ToneSuggestion {
  section: string;
  current_tone: string;
  suggested_tone: string;
  reason: string;
}

export interface StyleInconsistency {
  issue: string;
  occurrences: number;
  recommendation: string;
}

export interface GrammarCheckResult {
  checked_at: string;
  checked_by?: string;
  errors: GrammarError[];
  tone_suggestions: ToneSuggestion[];
  style_inconsistencies: StyleInconsistency[];
  readability_score?: number;
  overall_passed: boolean;
  error?: string;
}

export interface FormattingRequirementResult {
  passed: boolean;
  issues?: string[];
  current?: number;
  limit?: number;
  exceeded_by?: number;
}

export interface FormattingValidationResult {
  validated_at: string;
  validated_by?: string;
  requirements: {
    page_layout?: FormattingRequirementResult;
    fonts?: FormattingRequirementResult;
    headings?: FormattingRequirementResult;
    paragraphs?: FormattingRequirementResult;
    lists?: FormattingRequirementResult;
    tables_figures?: FormattingRequirementResult;
    word_count?: FormattingRequirementResult;
  };
  overall_passed: boolean;
  fix_recommendations: string[];
  error?: string;
}

export interface PreSubmissionReport {
  generated_at: string;
  generated_by: string;
  checklist_completion: {
    writer_checklist: {
      completed: boolean;
      percentage: number;
      total_items: number;
      completed_items: number;
    };
    reviewer_checklist: {
      completed: boolean;
      percentage: number;
      total_items: number;
      completed_items: number;
    };
  };
  formatting: FormattingValidationResult;
  grammar: GrammarCheckResult;
  overall_ready: boolean;
  blocking_issues: string[];
  recommendations: string[];
}

export interface TransitionValidation {
  allowed: boolean;
  blocking_issues: string[];
}

/**
 * Get QA checklist configuration and status
 */
export async function getQAChecklist(
  reportId: string,
  checklistType: 'writer' | 'reviewer'
): Promise<QAChecklistResponse> {
  const client = getAPIClient();
  return client.get<QAChecklistResponse>(
    `/reports/${reportId}/qa/checklist/${checklistType}`
  );
}

/**
 * Update checklist item completion
 */
export async function updateChecklistItem(
  reportId: string,
  checklistType: 'writer' | 'reviewer',
  update: ChecklistItemUpdate
): Promise<QAChecklistStatus> {
  const client = getAPIClient();
  return client.patch<QAChecklistStatus>(
    `/reports/${reportId}/qa/checklist/${checklistType}/items`,
    update
  );
}

/**
 * Check grammar, spelling, tone, and style
 */
export async function checkGrammar(reportId: string): Promise<GrammarCheckResult> {
  const client = getAPIClient();
  return client.post<GrammarCheckResult>(
    `/reports/${reportId}/qa/grammar/check`
  );
}

/**
 * Verify formatting against requirements
 */
export async function verifyFormatting(
  reportId: string
): Promise<FormattingValidationResult> {
  const client = getAPIClient();
  return client.post<FormattingValidationResult>(
    `/reports/${reportId}/qa/formatting/verify`
  );
}

/**
 * Run comprehensive pre-submission validation
 */
export async function validatePreSubmission(
  reportId: string
): Promise<PreSubmissionReport> {
  const client = getAPIClient();
  return client.post<PreSubmissionReport>(
    `/reports/${reportId}/qa/pre-submission/validate`
  );
}

/**
 * Validate state transition
 */
export async function validateTransition(
  reportId: string,
  fromStatus: string,
  toStatus: string
): Promise<TransitionValidation> {
  const client = getAPIClient();
  return client.post<TransitionValidation>(
    `/reports/${reportId}/qa/validate-transition`,
    { from_status: fromStatus, to_status: toStatus }
  );
}
